<template>
  <div>
    <v-data-table
      :headers="headers"
      :loading="isLoading"
      :items="purchaseOrders"
      :footer-props="{ itemsPerPageOptions :[10, 20, 30] }"
      single-expand
    >
      <template v-slot:item="props">
        <tr>
          <td
            class="pl-0 cursor-pointer"
            @click="toggleExpand(props)"
          >
            <div
              class="d-flex align-center"
            >
              <v-icon
                color="primary"
                class="mx-1"
                v-text="props.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              />
              {{ props.item.po_number }}

              <v-tooltip
                v-if="isInvalidPo(props.item)"
                top
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    class="orange--text subtitle-1 font-weight-black mx-2"
                    v-on="on"
                  >
                    mdi-alert
                  </v-icon>
                </template>

                <span> {{ props.item.invalidReasons.join('. ') }} </span>
              </v-tooltip>
            </div>
          </td>

          <td
            class="cursor-pointer"
            @click="toggleExpand(props)"
          >
            {{ props.item.retailer && props.item.retailer.name || 'N/A' }}
          </td>

          <td
            class="cursor-pointer py-2"
            @click="toggleExpand(props)"
          >
            {{ props.item.store && props.item.store.formatted_address || 'N/A' }}
          </td>

          <td
            class="cursor-pointer text-no-wrap"
            @click="toggleExpand(props)"
          >
            {{ humanizeDateFromSeconds(props.item.poDate, 'YYYY-MM-DD') || 'N/A' }}
          </td>

          <td
            class="cursor-pointer text-no-wrap"
            @click="toggleExpand(props)"
          >
            {{ humanizeDateFromSeconds(props.item.deliveryDate, 'YYYY-MM-DD') || 'N/A' }}
          </td>
        </tr>
      </template>

      <template v-slot:expanded-item="props">
        <tr
          class="grey lighten-4"
        >
          <td
            class="pa-5"
            :colspan="headers.length + 1"
          >
            <div class="primary--text subtitle-1 mb-2">
              Purchase Order Lines
            </div>

            <v-card outlined>
              <v-data-table
                hide-default-footer
                :headers="poLineHeaders"
                :items="props.item.poLines"
              >
                <template v-slot:item="productProp">
                  <tr>
                    <td>{{ productProp.index + 1 }}</td>

                    <td>
                      <span class="mt-0">
                        {{ productProp.item.product &&
                          productProp.item.product.description ||
                          'N/A'
                        }}
                      </span>

                      <v-tooltip
                        v-if="productProp.item.isInvalid"
                        top
                      >
                        <template v-slot:activator="{ on }">
                          <v-icon
                            class="orange--text subtitle-1 font-weight-black mx-2"
                            v-on="on"
                          >
                            mdi-alert
                          </v-icon>
                        </template>

                        <span> Invalid </span>
                      </v-tooltip>
                    </td>

                    <td>{{ productProp.item.productQuantity || 'N/A' }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>

            <div class="primary--text subtitle-1 mb-2 mt-5">
              Pallets
            </div>
            <v-card
              outlined
            >
              <v-card-text
                v-if="selectedPallets.isLoading === true"
                class="text-center"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                />
              </v-card-text>

              <v-data-table
                v-else
                single-expand
                item-key="id"
                hide-default-footer
                :headers="palletHeaders"
                :items="selectedPallets.list"
              >
                <template v-slot:item="palletProp">
                  <tr
                    class="cursor-pointer"
                    @click="palletProp.expand(!palletProp.isExpanded)"
                  >
                    <td>
                      <v-icon
                        color="primary"
                        class="mx-1"
                        v-text="
                          palletProp.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                      />

                      {{ palletProp.item.aggregateWeight.value }}
                      {{ palletProp.item.aggregateWeight.units }}
                    </td>
                    <td>
                      £ {{ palletProp.item.palletPrice }}
                    </td>
                    <td>
                      {{ getTotalProducts(palletProp.item) }}
                    </td>
                  </tr>
                </template>

                <template v-slot:expanded-item="palletProp">
                  <tr class="grey lighten-4">
                    <td
                      class="pa-5"
                      :colspan="palletHeaders.length + 1"
                    >
                      <div class="primary--text subtitle-1 mb-2">
                        Products
                      </div>

                      <v-card outlined>
                        <v-data-table
                          hide-default-footer
                          :headers="productHeaders"
                          :items="palletProp.item.products"
                        />
                      </v-card>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-snackbar
      v-model="snackbar.isOpen"
      :color="snackbar.color"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>

import {
  routesCollection,
  secureAuthSupplier,
  purchaseOrdersCollection,
  purchaseOrderLinesCollection,
  purchaseOrderPalletsCollection,
} from '@/firebase';
import { humanizeDateFromSeconds } from '@/utils/misc';
import { parsePurchaseOrderLines, parsePallets } from '@/utils/documentParser';

function defaultSelectedPallets() {
  return {
    isLoading: false,
    list: [],
  };
}

function groupByPurchaseOrder(poLines) {
  const result = poLines.reduce((purchaseOrders, poLine) => {
    if (purchaseOrders[poLine.purchaseOrderRef.id]) {
      purchaseOrders[poLine.purchaseOrderRef.id].poLines.push(poLine);
      return purchaseOrders;
    }

    /* eslint-disable-next-line no-param-reassign */
    purchaseOrders[poLine.purchaseOrderRef.id] = { ...poLine.purchaseOrder };
    /* eslint-disable-next-line no-param-reassign */
    purchaseOrders[poLine.purchaseOrderRef.id].poLines = [poLine];
    return purchaseOrders;
  }, {});

  return Object.values(result);
}
export default {
  props: {
    routeDetails: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      purchaseOrders: [],
      isLoading: false,
      headers: [
        { text: 'Vender PO ID', align: 'start', value: 'po_number' },
        { text: 'Customer (Retailer)', value: 'retailer.name', sortable: false },
        { text: 'Delivery Location', value: 'location', sortable: false },
        { text: 'PO Recieved', value: 'po_recieved', sortable: false },
        { text: 'PO Deadline', value: 'po_deadline', sortable: false },
      ],
      poLineHeaders: [
        { text: 'PO Line', sortable: false },
        {
          text: 'PO Line Description',
          align: 'start',
          value: 'name',
          sortable: false,
        },
        { text: 'Order Quantity', value: 'ean', sortable: false },
      ],
      palletHeaders: [
        { text: 'Agg. Pallet Weight', value: 'aggregateWeight' },
        { text: 'Comparative Pallet Price', value: 'palletPrice' },
        {
          text: 'Units on this pallet', sortable: false,
        },
      ],
      productHeaders: [
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Is Topper', value: 'isTopper', sortable: false },
        { text: 'Is Toppable', value: 'isTopable', sortable: false },
        { text: 'Quantity on this pallet', value: 'quantity', sortable: false },
      ],
      snackbar: {
        text: '',
        isOpen: false,
        color: 'green',
      },
      selectedPallets: defaultSelectedPallets(),
      unSubscribePurchaseOrderLines: () => {},
    };
  },
  mounted() {
    this.fetchPoLines();
  },
  beforeDestroy() {
    this.unSubscribePurchaseOrderLines();
  },
  methods: {
    humanizeDateFromSeconds,
    fetchPoLines() {
      this.isLoading = true;
      this.unSubscribePurchaseOrderLines();

      const query = this.generateFilters(purchaseOrderLinesCollection);
      this.unSubscribePurchaseOrderLines = query
        .onSnapshot((querySnapshot) => {
          this.isLoading = false;

          const poLines = querySnapshot.docs.map(parsePurchaseOrderLines);

          this.purchaseOrders = groupByPurchaseOrder(poLines);
        },
        () => {
          this.isLoading = false;

          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
        });
    },
    generateFilters(collection) {
      let query = secureAuthSupplier(collection);

      query = query.where('po_data.assigned_route_ref', '==', routesCollection.doc(this.routeDetails.id));

      query = query.orderBy('created_at', 'desc');

      return query;
    },
    toggleExpand(props) {
      if (props.isExpanded) {
        props.expand(!props.isExpanded);
        this.selectedPallets = { ...defaultSelectedPallets() };
        return;
      }

      props.expand(!props.isExpanded);
      this.fetchPallets(props.item);
    },
    fetchPallets(purchaseOrder) {
      this.selectedPallets.isLoading = true;
      const query = secureAuthSupplier(purchaseOrderPalletsCollection);

      query
        .where('po_ref', '==', purchaseOrdersCollection.doc(purchaseOrder.id))
        .get()
        .then((response) => {
          this.selectedPallets.isLoading = false;

          this.selectedPallets.list = response.docs.map(parsePallets);
        })
        .catch(() => {
          this.selectedPallets = defaultSelectedPallets();

          this.displaySnackbar({
            text: 'An error occured. Please try again',
            color: 'red',
          });
        });
    },
    isInvalidPo(purchaseOrder) {
      return purchaseOrder.isInvalid || purchaseOrder.deliveryDate < purchaseOrder.poDate;
    },
    getTotalProducts(pallet) {
      return pallet.products.reduce((sum, product) => sum + product.quantity, 0);
    },
    displaySnackbar({ text, color }) {
      this.snackbar = {
        text,
        color,
        isOpen: true,
      };
    },
  },
};
</script>
